/**
 * The app's icon set.
 *
 * Every glyph comes from Phosphor duotone (MIT) via `scripts/gen-icons.mjs`,
 * retargeted onto the `--bi-stroke` / `--bi-fill` tokens in `app.css`.
 * **No path geometry is authored in this repository.** The previous version of
 * this file held 62 hand-written `d="M…"` strings and the illustration module
 * held another 43. Path data written without seeing the rendered result is
 * subtly wrong in ways no test catches and no reviewer notices — that is how a
 * jar acquired a mitred corner and how the shopping screen drew the wrong
 * object outright.
 *
 * The exported function names are unchanged from the hand-drawn set, so every
 * call site keeps working. To add an icon, add a line to `CHROME_ICONS` in the
 * generator and run `npm run gen:icons`; a name that is not in the vendored set
 * fails the generator instead of shipping a blank frame.
 *
 * There is no accent colour. Phosphor duotone is two-tone by construction, and
 * re-introducing a third would mean hand-picking sub-paths per glyph — the
 * exact failure mode this replacement ends. `--bi-accent` was deleted with the
 * hand-drawn set rather than left defined and unread.
 *
 * Namespace note: SVG children must be created via `createElementNS`; plain
 * `document.createElement` yields non-rendering HTML elements. Bodies are
 * assigned as markup into a properly namespaced root for that reason.
 */

import { ICON_BODIES, ICON_GRID, ICON_SOURCES } from './icon-bodies.generated.js';

const SVG_NS = 'http://www.w3.org/2000/svg';

export { ICON_SOURCES, ICON_GRID };

/** Every icon name the generator emits. */
export const ICON_NAMES = Object.keys(ICON_BODIES);

/**
 * Build one icon at the chrome's default size.
 *
 * Named `buildIcon`, not `icon`: the module's `icon*` prefix marks a ready-made
 * factory, and a generic builder answering to that prefix gets swept up by
 * anything enumerating the set.
 *
 * An unknown name renders an empty frame rather than throwing: an icon sits
 * beside a label that already carries the meaning, so taking a screen down over
 * a missing decoration is the wrong trade. `web/test/brand-icons.test.mjs` is
 * what keeps a missing name from going unnoticed.
 *
 * @param {string} name
 * @param {string} [extraClass]
 * @returns {SVGSVGElement}
 */
export function buildIcon(name, extraClass = '') {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', ICON_GRID);
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('class', ('inline-block h-6 w-6 flex-shrink-0 ' + extraClass).trim());
  svg.dataset.icon = name;
  const body = ICON_BODIES[name];
  if (body) svg.innerHTML = body;
  return svg;
}

// One named export per icon, matching the hand-drawn set it replaces. Spelled
// out rather than produced in a loop so an editor can still resolve them and a
// typo at a call site is a build error instead of an undefined at runtime.
export const iconInventory = (c) => buildIcon('iconInventory', c);
export const iconShopping = (c) => buildIcon('iconShopping', c);
export const iconQuickAdd = (c) => buildIcon('iconQuickAdd', c);
export const iconHub = (c) => buildIcon('iconHub', c);
export const iconEdit = (c) => buildIcon('iconEdit', c);
export const iconDelete = (c) => buildIcon('iconDelete', c);
export const iconMove = (c) => buildIcon('iconMove', c);
export const iconScan = (c) => buildIcon('iconScan', c);
export const iconSearch = (c) => buildIcon('iconSearch', c);
export const iconSparkle = (c) => buildIcon('iconSparkle', c);
export const iconTag = (c) => buildIcon('iconTag', c);
export const iconBox = (c) => buildIcon('iconBox', c);
export const iconRecipes = (c) => buildIcon('iconRecipes', c);
export const iconIngredients = (c) => buildIcon('iconIngredients', c);
export const iconHistory = (c) => buildIcon('iconHistory', c);
export const iconLocations = (c) => buildIcon('iconLocations', c);
export const iconUnits = (c) => buildIcon('iconUnits', c);
export const iconProductMasters = (c) => buildIcon('iconProductMasters', c);
export const iconUsers = (c) => buildIcon('iconUsers', c);
export const iconMapping = (c) => buildIcon('iconMapping', c);
export const iconSubstitutions = (c) => buildIcon('iconSubstitutions', c);
export const iconShoppingCategories = (c) => buildIcon('iconShoppingCategories', c);
export const iconTimers = (c) => buildIcon('iconTimers', c);
export const iconRecipeCategories = (c) => buildIcon('iconRecipeCategories', c);
export const iconProfile = (c) => buildIcon('iconProfile', c);
export const iconLogout = (c) => buildIcon('iconLogout', c);
export const iconBell = (c) => buildIcon('iconBell', c);
export const iconCheck = (c) => buildIcon('iconCheck', c);
export const iconTheme = (c) => buildIcon('iconTheme', c);
